import React, { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Rss,
  XCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Zap,
  X,
  Sparkles,
  Quote,
  User,
  Gauge,
  Clock,
  Target,
  TrendingUp,
  Check,
  RotateCcw,
  Loader2,
  Inbox,
  Lock,
} from "lucide-react"
import { isBlurredField, BlurredInline } from "../Report/components/BlurredField"
import { getLocalItem, setLocalItem } from "../../utils/storage"
import {
  PRIORITY_COLORS,
  getCategoryMeta,
  getPriorityKey,
  getEffortLevel,
  sortActions,
  getActionKey,
  getActionsSummary,
  getFeedStatus,
} from "../../utils/actionsFeed"

const C = {
  text: "#ffffff",
  muted: "#9ca3af",
  dim: "#6b7280",
  border: "#374151",
  surface: "rgba(55, 65, 81, 0.35)",
  accent: "#F6255A",
  good: "#10b981",
}

const toList = v => (Array.isArray(v) ? v : v == null ? [] : [v])
const text = (value, size = "sm") =>
  isBlurredField(value) ? <BlurredInline field={value} size={size} interactive={false} /> : value
const plain = value => (typeof value === "string" ? value : "")

// Per-user, per-app step progress kept in localStorage so a user can work
// through an action plan across sessions. Shape: { [actionKey]: number[] }.
const useActionProgress = appId => {
  const storageKey = `aicmo_action_progress:v2:${appId || "unknown"}`
  const [progress, setProgress] = useState(() => getLocalItem(storageKey) || {})

  useEffect(() => {
    const stored = getLocalItem(storageKey)
    setProgress(stored && typeof stored === "object" ? stored : {})
  }, [storageKey])

  const update = useCallback(
    updater => {
      setProgress(prev => {
        const next = updater(prev)
        setLocalItem(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  const toggleStep = useCallback(
    (key, stepIndex) =>
      update(prev => {
        const done = new Set(prev[key] || [])
        done.has(stepIndex) ? done.delete(stepIndex) : done.add(stepIndex)
        return { ...prev, [key]: [...done] }
      }),
    [update],
  )

  const setAll = useCallback(
    (key, indexes) => update(prev => ({ ...prev, [key]: indexes })),
    [update],
  )

  return { progress, toggleStep, setAll }
}

// Indexes of steps a user can actually tick (locked steps are skipped).
const checkableSteps = item =>
  toList(item?.solution?.steps)
    .map((s, i) => (isBlurredField(s) ? null : i))
    .filter(i => i !== null)

const getStepStats = (item, doneList) => {
  const checkable = checkableSteps(item)
  const done = checkable.filter(i => (doneList || []).includes(i)).length
  return { done, total: checkable.length, complete: checkable.length > 0 && done === checkable.length }
}

const Chip = ({ color, bg, icon: Icon, children, title }) => (
  <span
    title={title}
    className="d-inline-flex align-items-center"
    style={{
      gap: 3,
      fontSize: 9,
      fontWeight: 600,
      letterSpacing: "0.03em",
      color,
      backgroundColor: bg,
      padding: "1px 6px",
      borderRadius: 999,
      whiteSpace: "nowrap",
      lineHeight: "15px",
    }}
  >
    {Icon && <Icon style={{ width: 9, height: 9 }} />}
    {children}
  </span>
)

const ProgressRing = ({ done, total, color }) => {
  const size = 28
  const r = 11.5
  const circ = 2 * Math.PI * r
  const pct = total ? done / total : 0
  return (
    <div title={`${done} of ${total} steps done`} style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: "block", transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.border} strokeWidth="2.5" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
        />
      </svg>
      <span
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 8,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: "-0.02em",
          color: C.muted,
        }}
      >
        {done}/{total}
      </span>
    </div>
  )
}

const EffortMeter = ({ level }) => (
  <span className="d-inline-flex align-items-end" style={{ gap: 2, height: 12 }}>
    {[1, 2, 3].map(n => (
      <span
        key={n}
        style={{
          width: 4,
          height: 4 + n * 3,
          borderRadius: 1,
          backgroundColor: n <= level ? (level === 3 ? "#ef4444" : level === 2 ? "#f59e0b" : C.good) : C.border,
        }}
      />
    ))}
  </span>
)

const FILTERS = [
  { key: "all", label: "All" },
  { key: "issues", label: "Issues" },
  { key: "wins", label: "Wins" },
]

const ActionsFeedPanel = ({ actionsFeed, appId, headerStyle }) => {
  const [expanded, setExpanded] = useState({})
  const [filter, setFilter] = useState("all")
  const [selected, setSelected] = useState(null)
  const { progress, toggleStep, setAll } = useActionProgress(appId)

  const feedLocked = isBlurredField(actionsFeed) ? actionsFeed : null
  const items = useMemo(
    () => (feedLocked ? [] : sortActions(toList(actionsFeed?.feed))),
    [actionsFeed, feedLocked],
  )
  const status = getFeedStatus(actionsFeed)
  const summary = getActionsSummary(actionsFeed, items)

  // Reset UI state when a different app is loaded.
  useEffect(() => {
    setExpanded({})
    setFilter("all")
    setSelected(null)
  }, [appId])

  const filtered = items.filter(item => {
    if (filter === "all" || isBlurredField(item)) return filter === "all"
    return filter === "issues" ? item?.problem : !item?.problem
  })

  // Overall plan progress across every visible problem's steps.
  const overall = items.reduce(
    (acc, item, i) => {
      if (isBlurredField(item) || !item?.problem) return acc
      const s = getStepStats(item, progress[getActionKey(item, i)])
      return { done: acc.done + s.done, total: acc.total + s.total, resolved: acc.resolved + (s.complete ? 1 : 0) }
    },
    { done: 0, total: 0, resolved: 0 },
  )

  const selectedKey = selected ? getActionKey(selected.item, selected.index) : null

  return (
    <>
      <div style={{ ...headerStyle, justifyContent: "space-between", marginBottom: 10 }}>
        <div className="d-flex align-items-center gap-2">
          <Rss style={{ width: 16, height: 16, color: C.muted }} />
          Actions Feed
          {plain(actionsFeed?.source).toLowerCase() === "llm" && (
            <span title="Generated by KeyQuest AI from reviews, ratings and competitor listings">
              <Sparkles style={{ width: 12, height: 12, color: C.accent }} />
            </span>
          )}
        </div>
        {items.length > 0 && (
          <div className="d-flex align-items-center" style={{ gap: 4 }}>
            <Chip color="#ef4444" bg="rgba(239,68,68,0.12)" title="Problems to fix">
              {summary.problems} issues
            </Chip>
            <Chip color={C.good} bg="rgba(16,185,129,0.12)" title="Strengths to leverage">
              {summary.advantages} wins
            </Chip>
          </div>
        )}
      </div>

      <div className="card-body" style={{ padding: 0 }}>
        {feedLocked ? (
          <BlurredInline field={feedLocked} size="lg" />
        ) : items.length === 0 ? (
          <EmptyState status={status} />
        ) : (
          <>
            {/* Plan progress */}
            {overall.total > 0 && (
              <div className="mb-2">
                <div className="d-flex justify-content-between" style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>
                  <span>
                    {summary.highPriority > 0 ? (
                      <>
                        <span style={{ color: "#ef4444", fontWeight: 600 }}>{summary.highPriority} high priority</span> to tackle first
                      </>
                    ) : (
                      "Action plan progress"
                    )}
                  </span>
                  <span style={{ color: overall.done === overall.total ? C.good : C.muted, fontWeight: 600 }}>
                    {overall.resolved}/{summary.problems} resolved
                  </span>
                </div>
                <div style={{ height: 4, borderRadius: 999, backgroundColor: C.border, overflow: "hidden" }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(overall.done / overall.total) * 100}%` }}
                    transition={{ duration: 0.5 }}
                    style={{ height: "100%", background: `linear-gradient(90deg, ${C.accent}, ${C.good})` }}
                  />
                </div>
              </div>
            )}

            {/* Filter */}
            <div
              className="d-flex mb-2"
              style={{ gap: 2, padding: 2, borderRadius: 8, backgroundColor: "rgba(55,65,81,0.4)" }}
              role="tablist"
            >
              {FILTERS.map(f => {
                const count = f.key === "all" ? items.length : f.key === "issues" ? summary.problems : summary.advantages
                const active = filter === f.key
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setFilter(f.key)}
                    style={{
                      flex: 1,
                      border: "none",
                      borderRadius: 6,
                      padding: "3px 0",
                      fontSize: 10,
                      fontWeight: 600,
                      color: active ? C.text : C.muted,
                      backgroundColor: active ? "rgba(246,37,90,0.25)" : "transparent",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {f.label} <span style={{ opacity: 0.7 }}>{count}</span>
                  </button>
                )
              })}
            </div>

            <div>
              {filtered.length === 0 && (
                <p className="mb-0 text-center" style={{ fontSize: 11, color: C.dim, padding: "16px 0" }}>
                  Nothing in this view.
                </p>
              )}
              {filtered.map((item, i) => {
                const index = items.indexOf(item)
                if (isBlurredField(item)) {
                  return (
                    <div key={`locked-${index}`} className="mb-2 p-2 rounded" style={{ border: `1px dashed ${C.border}` }}>
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: 11, color: C.muted }}>
                        <Lock style={{ width: 12, height: 12 }} />
                        <BlurredInline field={item} size="sm" />
                      </div>
                    </div>
                  )
                }
                const key = getActionKey(item, index)
                const pKey = getPriorityKey(item)
                const color = PRIORITY_COLORS[pKey]
                const stats = getStepStats(item, progress[key])
                const category = getCategoryMeta(item.category)
                const evidence = toList(item.evidence)
                const isOpen = !!expanded[key]
                const Icon = !item.problem ? CheckCircle2 : pKey === "high" ? XCircle : AlertTriangle
                const resolved = item.problem && stats.complete

                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.04 }}
                    className="mb-2 rounded"
                    style={{
                      border: `1px solid ${isOpen ? `${color}55` : C.border}`,
                      borderLeft: `3px solid ${resolved ? C.good : color}`,
                      backgroundColor: isOpen ? `${color}0f` : "rgba(31,32,44,0.6)",
                      opacity: resolved && !isOpen ? 0.7 : 1,
                      transition: "background-color 0.2s ease, border-color 0.2s ease",
                    }}
                  >
                    <button
                      type="button"
                      className="d-flex align-items-start w-100 text-start"
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{ gap: 8, padding: 8, border: "none", background: "transparent" }}
                    >
                      {resolved ? (
                        <CheckCircle2 style={{ width: 14, height: 14, color: C.good, flexShrink: 0, marginTop: 2 }} />
                      ) : (
                        <Icon style={{ width: 14, height: 14, color, flexShrink: 0, marginTop: 2 }} />
                      )}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <p
                          className="mb-1"
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            lineHeight: 1.35,
                            textDecoration: resolved ? "line-through" : "none",
                            textDecorationColor: C.dim,
                          }}
                        >
                          {text(item.title) || "Untitled action"}
                        </p>
                        <div className="d-flex flex-wrap align-items-center" style={{ gap: 4 }}>
                          {item.problem ? (
                            <Chip color={color} bg={`${color}1f`}>
                              {isBlurredField(item.priority) ? "PRIORITY" : `${pKey.toUpperCase()}`}
                            </Chip>
                          ) : (
                            <Chip color={C.good} bg="rgba(16,185,129,0.12)">STRENGTH</Chip>
                          )}
                          {category && (
                            <Chip color={C.muted} bg="rgba(156,163,175,0.1)" icon={category.icon}>
                              {category.label}
                            </Chip>
                          )}
                          {plain(item.solution?.owner) && (
                            <span style={{ fontSize: 9, color: C.dim }}>· {item.solution.owner}</span>
                          )}
                        </div>
                      </div>
                      {stats.total > 0 && stats.done > 0 && !resolved ? (
                        <ProgressRing done={stats.done} total={stats.total} color={color} />
                      ) : (
                        <ChevronDown
                          style={{
                            width: 14,
                            height: 14,
                            color: C.muted,
                            flexShrink: 0,
                            marginTop: 2,
                            transform: isOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          style={{ overflow: "hidden" }}
                        >
                          <div style={{ padding: "0 8px 8px 30px" }}>
                            {item.description && (
                              <p className="mb-2" style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                                {text(item.description, "lg")}
                              </p>
                            )}
                            {evidence.length > 0 && (
                              <div className="mb-2">
                                {evidence.slice(0, 2).map((e, j) => (
                                  <div key={j} className="d-flex align-items-start" style={{ gap: 5, fontSize: 10, color: "#d1d5db", marginBottom: 3 }}>
                                    <Quote style={{ width: 9, height: 9, color, flexShrink: 0, marginTop: 3 }} />
                                    <span>{text(e)}</span>
                                  </div>
                                ))}
                                {evidence.length > 2 && (
                                  <span style={{ fontSize: 10, color: C.dim, paddingLeft: 14 }}>+{evidence.length - 2} more signals</span>
                                )}
                              </div>
                            )}
                            {(item.solution && !isBlurredField(item.solution)) && (
                              <button
                                type="button"
                                onClick={() => setSelected({ item, index })}
                                className="d-flex align-items-center justify-content-center w-100"
                                style={{
                                  gap: 5,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: "6px 8px",
                                  borderRadius: 6,
                                  border: `1px solid ${item.problem ? "rgba(246,37,90,0.4)" : "rgba(16,185,129,0.4)"}`,
                                  color: item.problem ? C.accent : C.good,
                                  backgroundColor: item.problem ? "rgba(246,37,90,0.08)" : "rgba(16,185,129,0.08)",
                                }}
                              >
                                {item.problem ? <Zap style={{ width: 12, height: 12 }} /> : <TrendingUp style={{ width: 12, height: 12 }} />}
                                {item.problem
                                  ? stats.done > 0
                                    ? `Continue plan · ${stats.done}/${stats.total}`
                                    : "View action plan"
                                  : "How to leverage this"}
                                <ChevronRight style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {selected && (
              <ActionPlanDialog
                item={selected.item}
                doneSteps={progress[selectedKey] || []}
                onToggleStep={stepIndex => toggleStep(selectedKey, stepIndex)}
                onSetAll={indexes => setAll(selectedKey, indexes)}
                onClose={() => setSelected(null)}
              />
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  )
}

const EmptyState = ({ status }) => {
  const config = {
    pending: { icon: Loader2, spin: true, title: "Generating actions…", body: "KeyQuest AI is analysing reviews, ratings and competitors." },
    failed: { icon: AlertTriangle, title: "Couldn't generate actions", body: "Try re-scanning this app in a little while." },
    ready: { icon: Inbox, title: "No actions right now", body: "Nothing needs your attention for this app." },
  }[status]
  const Icon = config.icon
  return (
    <div className="text-center" style={{ padding: "24px 8px" }}>
      <motion.div
        animate={config.spin ? { rotate: 360 } : undefined}
        transition={config.spin ? { repeat: Infinity, duration: 1, ease: "linear" } : undefined}
        style={{ display: "inline-flex", marginBottom: 8 }}
      >
        <Icon style={{ width: 22, height: 22, color: C.dim }} />
      </motion.div>
      <p className="mb-1" style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{config.title}</p>
      <p className="mb-0" style={{ fontSize: 11, color: C.dim }}>{config.body}</p>
    </div>
  )
}

const MetaTile = ({ icon: Icon, label, children }) => (
  <div className="flex-fill p-2 rounded" style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, minWidth: 0 }}>
    <div className="d-flex align-items-center" style={{ gap: 4, fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
      <Icon style={{ width: 11, height: 11 }} />
      {label}
    </div>
    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, textTransform: "capitalize" }}>{children}</div>
  </div>
)

const ActionPlanDialog = ({ item, doneSteps, onToggleStep, onSetAll, onClose }) => {
  const solution = item.solution || {}
  const steps = toList(solution.steps)
  const evidence = toList(item.evidence)
  const pKey = getPriorityKey(item)
  const color = PRIORITY_COLORS[pKey]
  const category = getCategoryMeta(item.category)
  const effortLevel = getEffortLevel(solution.effort)
  const checkable = checkableSteps(item)
  const done = checkable.filter(i => doneSteps.includes(i)).length
  const allDone = checkable.length > 0 && done === checkable.length
  const Icon = !item.problem ? CheckCircle2 : pKey === "high" ? XCircle : AlertTriangle

  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose()
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="d-flex align-items-center justify-content-center"
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1050, backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={plain(item.title) || "Action plan"}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: "rgba(23, 24, 33, 1)",
          border: `1px solid ${C.border}`,
          borderTop: `3px solid ${color}`,
          borderRadius: 12,
          width: "92%",
          maxWidth: 560,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div className="d-flex align-items-start" style={{ gap: 12, padding: "18px 20px 14px" }}>
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${color}1f` }}
          >
            <Icon style={{ width: 18, height: 18, color }} />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex flex-wrap align-items-center mb-1" style={{ gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color }}>
                {item.problem ? `${pKey.toUpperCase()} PRIORITY ISSUE` : "STRENGTH TO LEVERAGE"}
              </span>
              {category && (
                <Chip color={C.muted} bg="rgba(156,163,175,0.1)" icon={category.icon}>{category.label}</Chip>
              )}
            </div>
            <h4 className="mb-0" style={{ fontSize: 17, fontWeight: 600, color: C.text, lineHeight: 1.3 }}>
              {text(item.title)}
            </h4>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: C.muted, padding: 4 }}
          >
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "0 20px 16px" }}>
          {item.description && (
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>{text(item.description, "lg")}</p>
          )}

          {/* Evidence */}
          {evidence.length > 0 && (
            <div className="mb-3 p-3 rounded" style={{ backgroundColor: `${color}0d`, border: `1px solid ${color}33` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                {item.problem ? "Why we flagged this" : "What's working"}
              </div>
              {evidence.map((e, j) => (
                <div key={j} className="d-flex align-items-start" style={{ gap: 8, fontSize: 12, color: "#e5e7eb", marginBottom: j === evidence.length - 1 ? 0 : 6 }}>
                  <Quote style={{ width: 11, height: 11, color, flexShrink: 0, marginTop: 3 }} />
                  <span>{text(e, "lg")}</span>
                </div>
              ))}
            </div>
          )}

          {/* Plan meta */}
          {(solution.owner || solution.effort || solution.timeframe) && (
            <div className="d-flex mb-3" style={{ gap: 8 }}>
              {solution.owner && <MetaTile icon={User} label="Owner">{text(solution.owner)}</MetaTile>}
              {solution.effort && (
                <MetaTile icon={Gauge} label="Effort">
                  <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
                    {effortLevel && <EffortMeter level={effortLevel} />}
                    {text(solution.effort)}
                  </span>
                </MetaTile>
              )}
              {solution.timeframe && <MetaTile icon={Clock} label="Timeframe">{text(solution.timeframe)}</MetaTile>}
            </div>
          )}

          {/* Steps checklist */}
          {steps.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5 className="mb-0" style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                  {item.problem ? "Action plan" : "How to leverage it"}
                </h5>
                {checkable.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: allDone ? C.good : C.muted }}>
                    {done} of {checkable.length} done
                  </span>
                )}
              </div>
              {checkable.length > 0 && (
                <div style={{ height: 4, borderRadius: 999, backgroundColor: C.border, overflow: "hidden", marginBottom: 10 }}>
                  <div
                    style={{
                      width: `${(done / checkable.length) * 100}%`,
                      height: "100%",
                      backgroundColor: allDone ? C.good : C.accent,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              )}
              {steps.map((step, index) => {
                if (isBlurredField(step)) {
                  return (
                    <div key={index} className="mb-2">
                      <BlurredInline field={step} size="lg" />
                    </div>
                  )
                }
                const checked = doneSteps.includes(index)
                return (
                  <motion.button
                    type="button"
                    key={index}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => onToggleStep(index)}
                    aria-pressed={checked}
                    className="d-flex align-items-start w-100 text-start mb-2"
                    style={{
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${checked ? "rgba(16,185,129,0.35)" : C.border}`,
                      backgroundColor: checked ? "rgba(16,185,129,0.07)" : C.surface,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        marginTop: 1,
                        border: `1.5px solid ${checked ? C.good : "#4b5563"}`,
                        backgroundColor: checked ? C.good : "transparent",
                        fontSize: 10,
                        fontWeight: 700,
                        color: checked ? "#fff" : C.muted,
                      }}
                    >
                      {checked ? <Check style={{ width: 12, height: 12 }} /> : index + 1}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: checked ? C.muted : C.text,
                        textDecoration: checked ? "line-through" : "none",
                      }}
                    >
                      {step}
                    </span>
                  </motion.button>
                )
              })}
            </div>
          )}

          {/* Outcomes */}
          {(solution.expected_impact || solution.success_signal) && (
            <div className="d-flex flex-column flex-sm-row" style={{ gap: 8 }}>
              {solution.expected_impact && (
                <div className="flex-fill p-3 rounded" style={{ backgroundColor: "rgba(246,37,90,0.08)", border: "1px solid rgba(246,37,90,0.3)" }}>
                  <div className="d-flex align-items-center mb-1" style={{ gap: 5, fontSize: 11, fontWeight: 600, color: C.accent }}>
                    <TrendingUp style={{ width: 12, height: 12 }} /> Expected impact
                  </div>
                  <p className="mb-0" style={{ fontSize: 12, color: C.text, lineHeight: 1.45 }}>{text(solution.expected_impact, "lg")}</p>
                </div>
              )}
              {solution.success_signal && (
                <div className="flex-fill p-3 rounded" style={{ backgroundColor: "rgba(16,185,129,0.07)", border: "1px solid rgba(16,185,129,0.3)" }}>
                  <div className="d-flex align-items-center mb-1" style={{ gap: 5, fontSize: 11, fontWeight: 600, color: C.good }}>
                    <Target style={{ width: 12, height: 12 }} /> How you'll know it worked
                  </div>
                  <p className="mb-0" style={{ fontSize: 12, color: C.text, lineHeight: 1.45 }}>{text(solution.success_signal, "lg")}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ gap: 8, padding: "12px 20px", borderTop: `1px solid ${C.border}` }}
        >
          {checkable.length > 0 ? (
            allDone ? (
              <button
                type="button"
                onClick={() => onSetAll([])}
                className="d-flex align-items-center"
                style={{ gap: 5, background: "transparent", border: "none", color: C.muted, fontSize: 12 }}
              >
                <RotateCcw style={{ width: 12, height: 12 }} /> Reset progress
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onSetAll(checkable)}
                className="d-flex align-items-center"
                style={{ gap: 5, background: "transparent", border: "none", color: C.good, fontSize: 12, fontWeight: 600 }}
              >
                <CheckCircle2 style={{ width: 13, height: 13 }} /> Mark all done
              </button>
            )
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${C.border}`,
              color: C.muted,
              padding: "6px 16px",
              fontSize: 13,
              borderRadius: 6,
            }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default ActionsFeedPanel
